import { TAZZIE_BRAND_VISUALS } from '../config/tazzieBrandVisuals';

/**
 * Known logo paths.
 *
 * 17 sectors (automotive, aviation, casino, charity, crypto, entertainment,
 * insurance, interpol, leo, logistics, mining, resources, retail, smartcity,
 * social, sports, wildlife) used to resolve to byte-identical copies of the
 * ANCHOR DEFENCE artwork. They now resolve to sovereign SVGs produced by
 * `backend/src/engine/media/logoEngine.ts` (see `scripts/regenerate-sector-logos.ts`).
 *
 * The previous `*.png` filenames are kept here aliased to the new `.svg`
 * so any hardcoded callers still work during the migration.
 */
const KNOWN_LOGO_PATHS: Record<string, string> = {
  'anchor-medical.png': '/assets/logos/anchor-medical.png',
  'anchor-finance.png': '/assets/logos/anchor-finance.png',
  'anchor-legal.png': '/assets/logos/anchor-legal.png',
  'anchor-climate.png': '/assets/logos/anchor-climate.png',
  'anchor-defence.png': '/assets/logos/anchor-defence.png',
  'anchor-energy.png': '/assets/logos/anchor-energy.png',
  'anchor-maritime.png': '/assets/logos/anchor-maritime.png',
  'anchor-space.jpeg': '/assets/logos/anchor-space.jpeg',
  'anchor-agriculture.png': '/assets/logos/anchor-agriculture.png',
  'anchor-education.png': '/assets/logos/anchor-education.png',
  // ── Sovereign regenerated marks (SVG) ──
  'anchor-leo.svg': '/assets/logos/anchor-leo.svg',
  'anchor-leo.png': '/assets/logos/anchor-leo.svg',
  'anchor-wildlife.svg': '/assets/logos/anchor-wildlife.svg',
  'anchor-wildlife.png': '/assets/logos/anchor-wildlife.svg',
  'anchor-casino.svg': '/assets/logos/anchor-casino.svg',
  'anchor-casino.png': '/assets/logos/anchor-casino.svg',
  'anchor-interpol.svg': '/assets/logos/anchor-interpol.svg',
  'anchor-interpol.png': '/assets/logos/anchor-interpol.svg',
  'anchor-aviation.svg': '/assets/logos/anchor-aviation.svg',
  'anchor-aviation.png': '/assets/logos/anchor-aviation.svg',
  'anchor-charity.svg': '/assets/logos/anchor-charity.svg',
  'anchor-charity.png': '/assets/logos/anchor-charity.svg',
  'anchor-crypto.svg': '/assets/logos/anchor-crypto.svg',
  'anchor-crypto.png': '/assets/logos/anchor-crypto.svg',
  'anchor-entertainment.svg': '/assets/logos/anchor-entertainment.svg',
  'anchor-entertainment.png': '/assets/logos/anchor-entertainment.svg',
  'anchor-insurance.svg': '/assets/logos/anchor-insurance.svg',
  'anchor-insurance.png': '/assets/logos/anchor-insurance.svg',
  'anchor-logistics.svg': '/assets/logos/anchor-logistics.svg',
  'anchor-logistics.png': '/assets/logos/anchor-logistics.svg',
  'anchor-mining.svg': '/assets/logos/anchor-mining.svg',
  'anchor-mining.png': '/assets/logos/anchor-mining.svg',
  'anchor-resources.svg': '/assets/logos/anchor-resources.svg',
  'anchor-resources.png': '/assets/logos/anchor-resources.svg',
  'anchor-retail.svg': '/assets/logos/anchor-retail.svg',
  'anchor-retail.png': '/assets/logos/anchor-retail.svg',
  'anchor-smartcity.svg': '/assets/logos/anchor-smartcity.svg',
  'anchor-smartcity.png': '/assets/logos/anchor-smartcity.svg',
  'anchor-social.svg': '/assets/logos/anchor-social.svg',
  'anchor-social.png': '/assets/logos/anchor-social.svg',
  'anchor-sports.svg': '/assets/logos/anchor-sports.svg',
  'anchor-sports.png': '/assets/logos/anchor-sports.svg',
  'anchor-automotive.svg': '/assets/logos/anchor-automotive.svg',
  'anchor-automotive.png': '/assets/logos/anchor-automotive.svg',
  // ── End sovereign set ──
  'anchor-fuel.png': '/assets/logos/anchor-fuel.png',
  'anchor-quantum.png': '/assets/logos/anchor-quantum.png',
  'anchor-agroshield.png': '/assets/logos/anchor-agroshield.png',
  'anchor-bioagri.png': '/assets/logos/anchor-bioagri.png',
  'anchor-blackflight.png': '/assets/logos/anchor-blackflight.png',
  'anchor-sovereign-surveillance.png': '/assets/logos/anchor-sovereign-surveillance.png',
  'anchor-semiconductor.png': '/assets/logos/anchor-semiconductor.png',
  'anchor-water-utilities.png': '/assets/logos/anchor-water-utilities.png',
  'anchor-humankind.png': '/assets/logos/anchor-humankind.png',
  'anchor.png': '/assets/logos/anchor.png',
};

const SLUG_TO_ASSET: Record<string, string> = {
  medical: 'anchor-medical.png',
  finance: 'anchor-finance.png',
  legal: 'anchor-legal.png',
  climate: 'anchor-climate.png',
  defence: 'anchor-defence.png',
  energy: 'anchor-energy.png',
  maritime: 'anchor-maritime.png',
  space: 'anchor-space.jpeg',
  agriculture: 'anchor-agriculture.png',
  education: 'anchor-education.png',
  leo: 'anchor-leo.svg',
  wildlife: 'anchor-wildlife.svg',
  casino: 'anchor-casino.svg',
  interpol: 'anchor-interpol.svg',
  aviation: 'anchor-aviation.svg',
  charity: 'anchor-charity.svg',
  crypto: 'anchor-crypto.svg',
  entertainment: 'anchor-entertainment.svg',
  insurance: 'anchor-insurance.svg',
  logistics: 'anchor-logistics.svg',
  mining: 'anchor-mining.svg',
  resources: 'anchor-resources.svg',
  retail: 'anchor-retail.svg',
  smartcity: 'anchor-smartcity.svg',
  social: 'anchor-social.svg',
  sports: 'anchor-sports.svg',
  automotive: 'anchor-automotive.svg',
  fuel: 'anchor-fuel.png',
  quantum: 'anchor-quantum.png',
  agroshield: 'anchor-agroshield.png',
  bioagri: 'anchor-bioagri.png',
  blackflight: 'anchor-blackflight.png',
  'sovereign-surveillance': 'anchor-sovereign-surveillance.png',
  semiconductor: 'anchor-semiconductor.png',
  'water-utilities': 'anchor-water-utilities.png',
  humankind: 'anchor-humankind.png',
};

const DEFAULT_LOGO = TAZZIE_BRAND_VISUALS.dreadcoreSi;
const PUBLIC_BRAND_MARK = '/assets/brand/anchor-mark.svg';

export function resolvePublicBrandMark(): string {
  return PUBLIC_BRAND_MARK;
}

export function resolveDefaultLogo(): string {
  return DEFAULT_LOGO;
}

export function resolveAnchorLogoByAsset(assetName: string): string {
  return KNOWN_LOGO_PATHS[assetName] ?? DEFAULT_LOGO;
}

export function resolveAnchorLogoBySlug(slug: string): string {
  const key = slug.replace(/^\/+/, '').replace(/^anchor-/, '');
  const asset = SLUG_TO_ASSET[key];
  return asset ? resolveAnchorLogoByAsset(asset) : DEFAULT_LOGO;
}
